import { useEffect, useState, Fragment } from "react";
import { createPortal } from "react-dom";
import { X, Mail, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { fmtDateTime } from "../lib/format";

// Preview + send for the client confirmation emails. Payroll run: pass runId. PD7A: pass remittance={companyId, year, month, label}.
// WSIB: pass wsib={companyId, year, period, label}.
export function ClosedNoticeDialog({ runId, remittance, wsib, onClose, onSent }) {
  const isWsib = !!wsib;
  const isPd7a = !!remittance || isWsib;   // PD7A + WSIB share the "figures" preview layout
  const remittance_ = remittance || wsib;
  const previewUrl = isWsib ? `/wsib/${wsib.companyId}/notify-closed/preview?year=${wsib.year}&period=${encodeURIComponent(wsib.period)}`
    : isPd7a ? `/remittance/${remittance.companyId}/notify-closed/preview?year=${remittance.year}&month=${remittance.month}` : `/payroll/runs/${runId}/notify-closed/preview`;
  const title = isWsib ? "WSIB Premium Remit Notification" : isPd7a ? "PD7A Processed – Confirmation" : "Payroll Processed – Confirmation";
  const [pv, setPv] = useState(null);
  const [padDate, setPadDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const q = !isPd7a && padDate ? `?pad_date=${padDate}` : "";
    api.get(`${previewUrl}${q}`).then((r) => { setPv(r.data); if (!isPd7a && !padDate) setPadDate(r.data.pad_date); })
      .catch((e) => { toast.error(e?.response?.data?.detail || apiErr(e)); onClose(); });
  }, [previewUrl, padDate]);  // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setBusy(true);
    try {
      const { data: r } = isWsib
        ? await api.post(`/wsib/${wsib.companyId}/notify-closed`, { year: wsib.year, period: wsib.period, note })
        : isPd7a
        ? await api.post(`/remittance/${remittance.companyId}/notify-closed`, { year: remittance.year, month: remittance.month, note })
        : await api.post(`/payroll/runs/${runId}/notify-closed`, { note, pad_date: padDate });
      if (r.failed?.length) toast.warning(`Sent ${r.sent} of ${r.recipients.length} — not delivered: ${r.failed.map((f) => f.email).join(", ")}`);
      else toast.success(`${title} sent to ${r.sent} recipient${r.sent === 1 ? "" : "s"}`);
      onSent(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || apiErr(e)); } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="closed-notice-dialog" onClick={onClose}>
      <div className="bg-white border border-black w-full max-w-5xl max-h-[92vh] grid lg:grid-cols-[1fr_360px]" onClick={(e) => e.stopPropagation()}>
        <div className="border-r border-border">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Email preview · {pv?.subject}</div>
            <button onClick={onClose} aria-label="Close" data-testid="closed-notice-close" className="text-muted-foreground hover:text-black"><X className="h-4 w-4" /></button>
          </div>
          {pv ? <iframe title="preview" data-testid="closed-notice-preview" srcDoc={pv.html} tabIndex={-1} className="w-full h-[calc(92vh-48px)] bg-neutral-50" /> : <div className="p-8 text-sm text-muted-foreground">Loading preview…</div>}
        </div>
        <div className="p-5 space-y-4 text-sm max-h-[92vh] overflow-y-auto">
          <h3 className="font-bold tracking-tight flex items-center gap-2"><Mail className="h-4 w-4" /> {title}{isPd7a ? ` · ${remittance_.label}` : ""}</h3>
          {!isPd7a && (
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Bank Withdrawal / PAD date</span>
              <input type="date" data-testid="closed-notice-pad-date" value={padDate} onChange={(e) => setPadDate(e.target.value)} className="mt-1 w-full border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              <span className="text-[10px] text-muted-foreground">Default: 3 business days before the pay date so banks can process.</span>
            </label>
          )}
          {pv && isPd7a && (
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs border border-border p-3 bg-neutral-50" data-testid="closed-notice-figures">
              {pv.figures.map(([k, v], i) => <Fragment key={k}><dt className={i === pv.figures.length - 1 ? "font-semibold" : "text-muted-foreground"}>{k}</dt><dd className={`font-mono text-right ${i === pv.figures.length - 1 ? "font-semibold" : ""}`}>{v}</dd></Fragment>)}
            </dl>
          )}
          {pv && !isPd7a && (
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs border border-border p-3 bg-neutral-50" data-testid="closed-notice-figures">
              <dt className="text-muted-foreground">Pay period end</dt><dd className="font-mono text-right">{pv.pay_period_end}</dd>
              <dt className="text-muted-foreground">Pay date</dt><dd className="font-mono text-right">{pv.pay_date}</dd>
              <dt className="text-muted-foreground">Employees</dt><dd className="font-mono text-right">{pv.employees}</dd>
              <dt className="text-muted-foreground">Employee net pay</dt><dd className="font-mono text-right">${pv.net.toLocaleString(undefined, { minimumFractionDigits: 2 })}</dd>
              <dt className="text-muted-foreground">CRA source deductions</dt><dd className="font-mono text-right">${pv.cra.toLocaleString(undefined, { minimumFractionDigits: 2 })}</dd>
              <dt className="font-semibold">Total funding required</dt><dd className="font-mono font-semibold text-right">${pv.funding.toLocaleString(undefined, { minimumFractionDigits: 2 })}</dd>
            </dl>
          )}
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Optional note (added above the contact line)</span>
            <textarea data-testid="closed-notice-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1 w-full border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black resize-none" />
          </label>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Attachments</div>
            {(pv?.attachments || []).map((a) => <div key={a} className="text-xs inline-flex items-center gap-1.5 mr-3"><Paperclip className="h-3 w-3" />{a}</div>)}
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Recipients ({pv?.recipients?.length ?? 0})</div>
            <ul className="text-xs space-y-0.5 max-h-28 overflow-y-auto" data-testid="closed-notice-recipients">
              {(pv?.recipients || []).map((r) => <li key={r.email}>{r.name ? `${r.name} · ` : ""}<span className="font-mono">{r.email}</span></li>)}
            </ul>
            <p className="text-[10px] text-muted-foreground mt-1">Each recipient is greeted by name. Follows Notifications settings (types, removed addresses).</p>
          </div>
          {pv?.last_sent_at && <p className="text-[10px] text-emerald-700">Last sent {fmtDateTime(pv.last_sent_at)} by {pv.last_sent_by}</p>}
          <div className="flex gap-2 pt-2">
            <button data-testid="closed-notice-send" onClick={send} disabled={busy || !pv || !pv.recipients?.length} className="flex-1 bg-black text-white px-4 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">{busy ? "Sending…" : "Send email"}</button>
            <button onClick={onClose} className="border border-border px-4 py-2.5 text-sm hover:border-black transition-colors">Cancel</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
